<template>
  <div class="center">
    <p>{{ $parent.language.StartGameTeamMultiNames.headding }}</p>
    <p v-bind:class="{ isred: nameError }">
      {{ $parent.language.StartGameTeamMultiNames.body }}
    </p>

    <!-- Step 1: Select team size -->
    <div
      class="container"
      v-bind:class="{ green: clicked }"
      v-if="currentStep === 'teamSize'"
    >
      <div class="row">
        <label>Number of Players:</label>
        <select
          class="input"
          v-model.number="teamSizePicked"
          @change="clicked = false"
        >
          <option value="" disabled>Select number of players</option>
          <option v-for="num in maxPlayerCount" :key="num" :value="num">
            {{ num }}
          </option>
        </select>
      </div>
      <div class="row">
        <button
          class="button"
          type="submit"
          :style="{ 'background-color': $parent.buttonColor }"
          @click="proceedToPlayerDetails()"
          :disabled="
            !teamSizePicked ||
            teamSizePicked < 1 ||
            teamSizePicked > maxPlayerCount
          "
        >
          Next
        </button>
      </div>
    </div>

    <!-- Step 2: Collect player details -->
    <div
      class="container"
      v-bind:class="{ green: clicked }"
      v-else-if="currentStep === 'playerDetails'"
    >
      <h3>Player {{ currentPlayerIndex + 1 }} of {{ teamSizePicked }}</h3>

      <div class="row">
        <input
          class="input"
          placeholder="First Name"
          v-model="players[currentPlayerIndex].firstName"
          :class="{ error: validationErrors.firstName }"
        />
      </div>

      <div class="row">
        <input
          class="input"
          placeholder="Last Name"
          v-model="players[currentPlayerIndex].lastName"
          :class="{ error: validationErrors.lastName }"
        />
      </div>

      <div class="row">
        <input
          class="input"
          placeholder="Email Address"
          v-model="players[currentPlayerIndex].email"
          :class="{ error: validationErrors.email }"
        />
      </div>

      <div
        class="row"
        v-if="
          validationErrors.firstName ||
          validationErrors.lastName ||
          validationErrors.email
        "
      >
        <p class="error-text">Please fill in all required fields</p>
      </div>

      <div class="row">
        <button
          v-if="currentPlayerIndex > 0"
          class="button secondary"
          @click="previousPlayer()"
        >
          Previous
        </button>

        <button
          class="button"
          type="submit"
          :style="{ 'background-color': $parent.buttonColor }"
          @click="nextPlayerOrProceed()"
        >
          {{
            currentPlayerIndex < teamSizePicked - 1 ? "Next Player" : "Continue"
          }}
        </button>
      </div>
    </div>

    <!-- Step 3: Required team name -->
    <div
      class="container"
      v-bind:class="{ green: clicked }"
      v-else-if="currentStep === 'teamName'"
    >
      <h3>Team Setup Complete!</h3>
      <p>Players added: {{ teamSizePicked }}</p>

      <div class="row">
        <input
          class="input"
          placeholder="Team Name (Required)"
          v-model="teamName"
          :class="{ error: validationErrors.teamName }"
        />
      </div>

      <div class="row" v-if="validationErrors.teamName">
        <p class="error-text">Please enter a team name</p>
      </div>

      <div class="row">
        <button class="button secondary" @click="backToPlayerDetails()">
          Back to Players
        </button>

        <button
          class="button"
          type="submit"
          :style="{ 'background-color': $parent.buttonColor }"
          @click="submitTeam()"
        >
          {{ $parent.language.StartGameTeamMultiNames.go }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
@import "../assets/form.css";

.error {
  border-color: red !important;
}

.error-text {
  color: red;
  font-size: 0.9em;
}

.secondary {
  background-color: #ccc !important;
  margin-right: 10px;
}

.row {
  margin-bottom: 10px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

<script>
// import axios from "axios";
import { callApi } from "../plugins/callApi.js";
export default {
  name: "StartGameTeam",
  props: {
    msg: String,
    dbid: String,
  },
  data() {
    return {
      currentStep: "teamSize", // 'teamSize', 'playerDetails', 'teamName'
      teamSizePicked: "",
      currentPlayerIndex: 0,
      teamName: "",
      maxPlayerCount: 0,
      nameError: false,
      clicked: false,
      validationErrors: {
        firstName: false,
        lastName: false,
        email: false,
        teamName: false,
      },
      players: [],
    };
  },
  mounted() {
    this.maxPlayerCount = this.$parent.gameSettings.maxPlayers || 4;
  },
  methods: {
    proceedToPlayerDetails() {
      // Validate team size
      if (
        !this.teamSizePicked ||
        this.teamSizePicked < 1 ||
        this.teamSizePicked > this.maxPlayerCount
      ) {
        return;
      }

      // Initialize players array based on team size
      this.initializePlayers();

      this.currentStep = "playerDetails";
      this.currentPlayerIndex = 0;
      this.clicked = false;
    },

    initializePlayers() {
      // Create array with the exact number of players needed
      this.players = Array.from({ length: this.teamSizePicked }, () => ({
        firstName: "",
        lastName: "",
        email: "",
      }));
    },

    validateCurrentPlayer() {
      const player = this.players[this.currentPlayerIndex];
      this.validationErrors = {
        ...this.validationErrors,
        firstName: !player.firstName.trim(),
        lastName: !player.lastName.trim(),
        email: !player.email.trim() || !this.isValidEmail(player.email),
      };

      return (
        !this.validationErrors.firstName &&
        !this.validationErrors.lastName &&
        !this.validationErrors.email
      );
    },

    validateTeamName() {
      this.validationErrors.teamName = !this.teamName.trim();
      return !this.validationErrors.teamName;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    nextPlayerOrProceed() {
      if (!this.validateCurrentPlayer()) {
        this.clicked = false;
        return;
      }

      this.clicked = true;

      if (this.currentPlayerIndex < this.teamSizePicked - 1) {
        // Move to next player
        this.currentPlayerIndex++;
        this.clicked = false;
        this.clearValidationErrors();
      } else {
        // All players entered, proceed to team name
        this.currentStep = "teamName";
        this.clicked = false;
      }
    },

    previousPlayer() {
      if (this.currentPlayerIndex > 0) {
        this.currentPlayerIndex--;
        this.clearValidationErrors();
      }
    },

    backToPlayerDetails() {
      this.currentStep = "playerDetails";
      this.currentPlayerIndex = this.teamSizePicked - 1;
      this.clearValidationErrors();
    },

    clearValidationErrors() {
      this.validationErrors = {
        firstName: false,
        lastName: false,
        email: false,
        teamName: false,
      };
    },

    async submitTeam() {
      if (!this.validateTeamName()) {
        this.clicked = false;
        return;
      }

      try {
        this.clicked = true;

        // Prepare the data to send - use all players in the array
        const teamData = {
          dbid: this.dbid,
          teamname: this.teamName.trim(),
          email: this.players.map((player) => ({
            firstName: player.firstName.trim(),
            lastName: player.lastName.trim(),
            email: player.email.trim(),
          })),
        };

        // await axios.post(process.env.VUE_APP_ip + "/newgame", teamData);

        var setup = await callApi("newgame", this.license, {
          name: this.name,
          extra: teamData,
          dbid: this.dbid,
          chosenLanguage: this.$parent.chosenLanguage,
        });
        this.$emit("updatestage", "");
      } catch (error) {
        console.error("Error submitting team:", error);
        this.clicked = false;
        // You might want to show an error message to the user here
      }
    },
  },
};
</script>